import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";

export const CITIES = {
  beuzeville: {
    ville: "Beuzeville",
    titre: "Maçonnerie à Beuzeville",
    seoTitle: "Maçonnerie à Beuzeville | Parement brique & rénovation – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie à Beuzeville : parement brique, rénovation de façades, murs et murets. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "DMZ Normandie intervient à Beuzeville et dans l'ouest de l'Eure : parement brique, rénovation de façades, maçonnerie générale et aménagements extérieurs.",
    intro: "À Beuzeville, nous avons notamment réalisé l'habillage en parement brique d'une façade et d'un pignon complets — un chantier visible dans nos réalisations. Nous connaissons le bâti du secteur et nous nous déplaçons gratuitement pour étudier votre projet.",
    chantier: "Parement brique d'une façade et d'un pignon — Beuzeville",
    photos: [
      { img: "/realisations/parement-brique-beuzeville-facade.webp", alt: "Parement brique sur façade à Beuzeville par DMZ Normandie" },
      { img: "/realisations/parement-brique-beuzeville-pignon.webp", alt: "Parement brique sur pignon à Beuzeville par DMZ Normandie" },
    ],
    travaux: ["Parement brique façades et pignons", "Rénovation et réparation de maçonnerie", "Murs, murets et clôtures", "Fondations et dalles béton", "Aménagements extérieurs"],
    faq: [
      { q: "Faites-vous du parement brique à Beuzeville ?", a: "Oui, c'est un chantier que nous avons réalisé à Beuzeville : habillage complet d'une façade et d'un pignon. Voir les photos dans nos réalisations." },
      { q: "Vous déplacez-vous à Beuzeville pour un devis ?", a: "Oui, la visite et le devis sont gratuits à Beuzeville et dans les communes alentours." },
    ],
  },
  louviers: {
    ville: "Louviers",
    titre: "Maçonnerie à Louviers",
    seoTitle: "Maçonnerie à Louviers | Gros œuvre & extérieurs – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie à Louviers : gros œuvre, construction de maisons, allées pavées, bordures et terrasses. DMZ Normandie — devis gratuit.",
    subtitle: "DMZ Normandie intervient à Louviers et dans le sud de l'Eure : gros œuvre, construction de maisons, terrasses, allées en pavés et bordures.",
    intro: "À Louviers, nous avons construit une maison en parpaings (murs, pignons, poutre de façade) et réalisé un aménagement extérieur complet : escalier bordé de pavés, bordures et muret de soutènement. Deux chantiers visibles dans nos réalisations.",
    chantier: "Construction de maison + escalier pavé — Louviers",
    photos: [
      { img: "/realisations/maison-parpaing-louviers.webp", alt: "Construction d'une maison en parpaings à Louviers par DMZ Normandie" },
      { img: "/realisations/bordures-escalier-paves-louviers.webp", alt: "Bordures en escalier et pavés à Louviers par DMZ Normandie" },
    ],
    travaux: ["Gros œuvre et construction neuve", "Terrasses et allées en pavés", "Bordures, escaliers et murets", "Fondations et dalles béton", "Extensions de maison"],
    faq: [
      { q: "Construisez-vous des maisons à Louviers ?", a: "Oui, nous avons construit une maison en parpaings à Louviers : fondations, murs, pignons et ouvertures. Voir la photo dans nos réalisations." },
      { q: "Faites-vous les aménagements extérieurs à Louviers ?", a: "Oui : allées pavées, bordures, escaliers extérieurs et murets, comme notre chantier d'escalier bordé de pavés à Louviers." },
    ],
  },
  "conches-en-ouche": {
    ville: "Conches-en-Ouche",
    titre: "Maçonnerie à Conches-en-Ouche",
    seoTitle: "Maçonnerie à Conches-en-Ouche | Pavés & extérieurs – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie à Conches-en-Ouche : allées en pavés, terrasses, murets et aménagements extérieurs. DMZ Normandie — devis gratuit.",
    subtitle: "DMZ Normandie intervient à Conches-en-Ouche et alentours : allées en pavés, terrasses, murets et aménagements extérieurs en béton.",
    intro: "À Conches-en-Ouche, nous avons posé une allée en pavés avec bordures béton — un chantier visible dans nos réalisations. Nous intervenons dans tout le secteur pour vos extérieurs et vos travaux de maçonnerie.",
    chantier: "Allée en pavés avec bordures — Conches-en-Ouche",
    photos: [
      { img: "/realisations/paves-conches-en-ouche.webp", alt: "Allée en pavés posée à Conches-en-Ouche par DMZ Normandie" },
    ],
    travaux: ["Allées et terrasses en pavés", "Bordures et murets", "Dalles et terrasses béton", "Maçonnerie générale et réparations", "Extensions et gros œuvre"],
    faq: [
      { q: "Posez-vous des pavés à Conches-en-Ouche ?", a: "Oui, nous avons réalisé une allée en pavés avec bordures béton à Conches-en-Ouche. Voir la photo dans nos réalisations." },
      { q: "Faites-vous les terrasses béton à Conches-en-Ouche ?", a: "Oui : dalles, terrasses et allées en béton lissé ou désactivé, avec préparation complète du sol." },
    ],
  },
  "pont-audemer": {
    ville: "Pont-Audemer",
    titre: "Maçonnerie à Pont-Audemer",
    seoTitle: "Maçonnerie à Pont-Audemer | Gros œuvre & construction – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie à Pont-Audemer : gros œuvre, construction de maisons, fondations et murs. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "DMZ Normandie intervient à Pont-Audemer et dans l'ouest de l'Eure : gros œuvre, construction neuve, fondations et élévation de murs.",
    intro: "À Pont-Audemer, nous avons réalisé le gros œuvre complet d'une maison à double pignon : fondations, élévation en parpaings, pignons et ouvertures — prête pour la charpente. Un chantier visible dans nos réalisations.",
    chantier: "Gros œuvre d'une maison à double pignon — Pont-Audemer",
    photos: [
      { img: "/realisations/gros-oeuvre-pignons-pont-audemer.webp", alt: "Gros œuvre d'une maison à double pignon à Pont-Audemer par DMZ Normandie" },
    ],
    travaux: ["Gros œuvre et construction neuve", "Fondations et dalles béton", "Élévation de murs et pignons", "Extensions et garages", "Réparations de maçonnerie"],
    faq: [
      { q: "Faites-vous du gros œuvre à Pont-Audemer ?", a: "Oui, nous avons construit une maison à double pignon à Pont-Audemer : fondations, murs et pignons. Voir la photo dans nos réalisations." },
      { q: "Intervenez-vous dans le Pays d'Auge ?", a: "Oui, l'ouest de l'Eure et le Pays d'Auge font partie de notre zone d'intervention, selon la nature du chantier." },
    ],
  },
  thiberville: {
    ville: "Thiberville",
    titre: "Maçonnerie à Thiberville",
    seoTitle: "Maçonnerie à Thiberville | Gros œuvre & murs – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie à Thiberville : gros œuvre, élévation de murs, poutres béton, fondations. DMZ Normandie — devis gratuit.",
    subtitle: "DMZ Normandie intervient à Thiberville et dans les communes de l'ouest bernayen : gros œuvre, élévation de murs, poutres béton et fondations.",
    intro: "À Thiberville, nous avons monté les murs d'une construction avec une grande poutre béton de chaînage — coffrage et coulage réalisés par nos soins. Un chantier visible dans nos réalisations.",
    chantier: "Élévation de murs et poutre béton — Thiberville",
    photos: [
      { img: "/realisations/elevation-poutre-thiberville.webp", alt: "Élévation de murs avec poutre béton à Thiberville par DMZ Normandie" },
    ],
    travaux: ["Gros œuvre et élévation de murs", "Poutres et linteaux béton", "Fondations et semelles", "Dalles béton", "Murs et murets"],
    faq: [
      { q: "Intervenez-vous à Thiberville ?", a: "Oui, nous y avons réalisé l'élévation de murs avec poutre béton de chaînage. Voir la photo dans nos réalisations." },
      { q: "Faites-vous les petits travaux de maçonnerie à Thiberville ?", a: "Oui : murets, réparations, ouvertures et rénovation de maçonnerie, en plus des constructions neuves." },
    ],
  },
  deauville: {
    ville: "Deauville",
    titre: "Maçonnerie à Deauville",
    seoTitle: "Maçonnerie à Deauville | Gros œuvre & dalles béton – DMZ Normandie",
    seoDesc: "Entreprise de maçonnerie intervenant à Deauville : gros œuvre, dalles béton, fondations et murs. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "DMZ Normandie intervient jusqu'à Deauville et la Côte Fleurie pour les chantiers d'envergure : gros œuvre, dalles béton, fondations et élévation de murs.",
    intro: "À Deauville, nous avons réalisé le gros œuvre d'une maison : fondations, dalles béton coulées et élévation des murs en parpaings — un chantier photographié en vue aérienne dans nos réalisations.",
    chantier: "Gros œuvre d'une maison — Deauville",
    photos: [
      { img: "/realisations/gros-oeuvre-deauville.webp", alt: "Gros œuvre vu du ciel : dalles et murs en parpaings à Deauville par DMZ Normandie" },
    ],
    travaux: ["Gros œuvre de maisons", "Dalles et fondations béton", "Élévation de murs en parpaing", "Terrasses et extérieurs", "Extensions"],
    faq: [
      { q: "Vous déplacez-vous jusqu'à Deauville ?", a: "Oui, pour les chantiers d'envergure nous intervenons jusqu'à Deauville et la Côte Fleurie. Nous avons d'ailleurs réalisé un gros œuvre complet à Deauville." },
      { q: "Faites-vous des devis gratuits à Deauville ?", a: "Oui, la visite et le devis sont gratuits. Appelez-nous ou remplissez le formulaire en décrivant votre projet." },
    ],
  },
};

export default function LocalPage({ slug }) {
  const c = CITIES[slug];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: c.titre,
    provider: {
      "@type": "GeneralContractor",
      name: "DMZ Normandie",
      telephone: "+33745049189",
      email: "dmznormandie@gmail.com",
      url: "https://dmznormandie.fr",
      address: { "@type": "PostalAddress", streetAddress: "7 rue Jean de la Fontaine", postalCode: "27000", addressLocality: "Évreux", addressRegion: "Normandie", addressCountry: "FR" },
    },
    areaServed: c.ville,
  };

  return (
    <>
      <SEO title={c.seoTitle} description={c.seoDesc} path={`/maconnerie-${slug}`} jsonLd={jsonLd} />
      <PageHero title={c.titre} subtitle={c.subtitle} />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mb-16">
            <Reveal>
              <SectionHead overline="Nos travaux" title={`Ce que nous réalisons à ${c.ville}`} />
              <p className="-mt-6 mb-8 text-zinc-400 font-light leading-relaxed">{c.intro}</p>
              <div className="space-y-4">
                {c.travaux.map((t) => (
                  <p key={t} className="flex items-start gap-3 text-zinc-300 font-light">
                    <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {t}
                  </p>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/contact" className="btn-sable" data-testid={`cta-devis-${slug}`}>
                  Demander un devis à {c.ville} <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#C4A484] mb-4">Notre chantier ici</p>
              <p className="font-serif-display font-light text-xl text-[#F5F5F0] mb-5">{c.chantier}</p>
              <div className={`grid gap-4 ${c.photos.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                {c.photos.map((p) => (
                  <div key={p.img} className="overflow-hidden relative">
                    <img src={p.img} alt={p.alt} loading="lazy" className="w-full h-56 md:h-72 object-cover" />
                    <div className="absolute inset-0 bg-black/20" />
                  </div>
                ))}
              </div>
              <Link to="/realisations" className="inline-block mt-5 text-xs tracking-[0.25em] uppercase text-[#C4A484] hover:text-[#F5F5F0] transition-colors" data-testid={`lien-realisations-${slug}`}>
                Voir toutes nos réalisations →
              </Link>
            </Reveal>
          </div>

          <div className="max-w-4xl mx-auto">
            <SectionHead overline="Questions fréquentes" title={`Vos questions à ${c.ville}`} />
            <FAQAccordion items={c.faq} testid={`faq-${slug}`} />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
